import { useState, useRef, useEffect } from 'react';
import { ChevronUp, Building2, Loader2 } from 'lucide-react';
import { useUserStore } from '../../../features/auth/store/userStore';
import { useCompanySwitcher } from '../../../features/auth/hooks/useCompanySwitcher';
import { useSiteSwitcher } from '../../../features/auth/hooks/useSiteSwitcher';
import { getDefaultSite } from '../../../features/auth/utils/defaultSite';
import { getFirmName } from '../../../shared/utils/firmName';
import { ROLES, hasRole } from '../../../shared/constants/roles';

// AppSidebar also passes `isMobile`. It used to switch the hover handlers off on mobile; the menu is
// click-only at every width now, so nothing here reads it.
export function FirmSelector({ isCollapsed }) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const { profile, roles } = useUserStore();
  const { companies, loading: companiesLoading, switching: companySwitching, currentFirmId, loadCompanies, switchCompany } = useCompanySwitcher();
  const { switching: siteSwitching, switchSite } = useSiteSwitcher();
  const switching = companySwitching || siteSwitching;

  // The dropdown is hidden for single-firm users, so the firm count has to be known before the user
  // interacts — the old hover/click-only load was too late. Guarded with a local ref (not the hook's
  // `loadedRef`) because `loadCompanies` gets a new identity on every `loading` flip; without this
  // guard a failed fetch would re-fire this effect forever, retrying and re-toasting endlessly.
  const fetchedOnMountRef = useRef(false);
  useEffect(() => {
    if (fetchedOnMountRef.current) return;
    fetchedOnMountRef.current = true;
    loadCompanies();
  }, [loadCompanies]);

  // Only a user who belongs to more than one firm gets a dropdown. With one firm the menu was a dead
  // end — a single row that is already disabled for being the current firm. A failed fetch leaves
  // `companies` empty (the hook toasts the error) and lands here too, and `companiesLoading` is
  // deliberately not treated as "maybe multiple" so the common single-firm case never flickers a
  // chevron in and back out.
  const hasMultipleFirms = companies.length > 1;

  // The active customer's sites drive the site line on the card and the nested site rows. One site is
  // shown as plain text; several make the card openable even for a single-customer user, since that is
  // the only place to switch sites. `currentSite` falls back to the default (first) site while the
  // backend has not marked one current yet, so the card never shows an empty site line.
  const activeCompany = companies.find((c) => c.firmId === currentFirmId);
  const activeSites = Array.isArray(activeCompany?.sites) ? activeCompany.sites : [];
  const hasMultipleSites = activeSites.length > 1;
  const currentSite = activeSites.find((s) => s.isCurrent) ?? getDefaultSite(activeSites);
  const hasDropdown = hasMultipleFirms || hasMultipleSites;
  // a11y: exactly one firm means the trigger is a permanent no-op — mark it inert for screen readers.
  // An empty list (still loading, or a failed fetch) is excluded: clicking there retries the load, so
  // it is genuinely interactive and must not be announced as disabled.
  const isSingleFirmInert = companies.length === 1 && !hasMultipleSites;

  // The card shows the ACTIVE FIRM, not the user (QA: it used to show the user's name). `profile` is
  // null until the bootstrap /me/profile fetch resolves, and the payload is inconsistent about the
  // firm-name key — `getFirmName` handles both.
  const firmName = getFirmName(profile);
  // Get primary role for display. Slugs come from ROLES so a backend rename is a one-place edit —
  // inlined literals previously drifted out of sync and made every user read as plain "User".
  const getPrimaryRole = () => {
    if (hasRole(roles, ROLES.ACCOUNT_OWNER)) return 'Account Owner';
    if (hasRole(roles, ROLES.BILLING_ADMIN)) return 'Billing Admin';
    if (hasRole(roles, ROLES.LICENSE_ADMIN)) return 'License Admin';
    if (hasRole(roles, ROLES.REGISTERED_CONTACT)) return 'Registered User';
    return 'User';
  };

  // Collapsing the rail swaps the card for the icon tile below; drop an open menu with it so it
  // doesn't reappear already open when the rail expands again. Adjusted during render rather than
  // in an effect so the stale menu never paints for a frame.
  if (isCollapsed && isOpen) setIsOpen(false);

  // Click-only menu (it used to open on hover). With no mouse-leave to close it, an open menu closes
  // on a click/tap anywhere outside the selector, or on Escape — which hands focus back to the
  // trigger so keyboard users aren't dropped at the top of the page.
  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setIsOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Handle click toggle
  const handleClick = () => {
    if (!hasDropdown) {
      // The hook's own `loadedRef` is only set on success, so an empty list here means the mount
      // fetch failed — retry rather than stranding a real multi-firm user with no way to recover.
      if (!companiesLoading && companies.length === 0) loadCompanies();
      return;
    }
    const newState = !isOpen;
    setIsOpen(newState);
    if (newState) {
      loadCompanies();
    }
  };

  const handleFirmSelect = (company) => {
    if (company.firmId !== currentFirmId) {
      switchCompany(company);
    }
    setIsOpen(false);
  };

  // A site under the active customer is switched in place; a site under another customer goes through
  // the customer switch (firm cookie, subdomain hand-off) with that site as the target.
  const handleSiteSelect = (company, site) => {
    if (company.firmId === currentFirmId) {
      if (site.haloPsaSiteId !== currentSite?.haloPsaSiteId) {
        switchSite(site);
      }
    } else {
      switchCompany(company, site);
    }
    setIsOpen(false);
  };

  if (isCollapsed) {
    return (
      <div className="p-3">
        <div className="w-10 h-10 rounded-lg bg-nav-tile border border-chrome-edge flex items-center justify-center">
          <Building2 className="w-5 h-5 text-nav-fg-muted" />
        </div>
      </div>
    );
  }

  // The dropdown only renders with more than one firm, so the list is never empty here — the only
  // other state worth showing is a refetch still in flight.
  let dropdownContent;
  if (companiesLoading) {
    dropdownContent = (
      <div className="flex items-center gap-2 px-4 py-3 text-sm text-nav-fg-subtle" data-testid="firm-selector-loading">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>Loading…</span>
      </div>
    );
  } else {
    dropdownContent = companies.map((company, index) => {
      const isCurrent = company.firmId === currentFirmId;
      // Sites are only listed for customers with a real choice; a single site is just the default.
      const sites = Array.isArray(company.sites) && company.sites.length > 1 ? company.sites : [];
      return (
        <div
          key={company.firmId}
          className={index < companies.length - 1 ? 'border-b border-nav-menu-border' : ''}
        >
          <button
            onClick={() => handleFirmSelect(company)}
            disabled={switching || isCurrent}
            className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors hover:bg-nav-menu-hover ${
              switching ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            data-testid={`firm-selector-item-${company.firmId}`}
          >
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-nav-fg truncate">{company.name}</p>
              {company.role && <p className="text-xs text-nav-fg-subtle truncate">{company.role}</p>}
            </div>
            {/* With one firm the badge marks the only option, so it says nothing — keep it for real choices. */}
            {isCurrent && hasMultipleFirms && (
              <span className="ml-3 px-3 py-1 text-xs font-medium rounded-full border border-nav-badge bg-nav-badge-bg text-nav-badge flex-shrink-0">
                Current
              </span>
            )}
          </button>
          {sites.length > 0 && (
            <div role="group" aria-label={`Sites of ${company.name}`} data-testid={`site-selector-group-${company.firmId}`}>
              {sites.map((site) => {
                const isCurrentSite = isCurrent && site.haloPsaSiteId === currentSite?.haloPsaSiteId;
                return (
                  <button
                    key={site.haloPsaSiteId}
                    onClick={() => handleSiteSelect(company, site)}
                    disabled={switching || isCurrentSite}
                    className={`w-full flex items-center justify-between pl-8 pr-4 py-2 text-left transition-colors hover:bg-nav-menu-hover ${
                      switching ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                    data-testid={`site-selector-item-${site.haloPsaSiteId}`}
                  >
                    <p className="text-xs text-nav-fg-secondary truncate">{site.siteName}</p>
                    {isCurrentSite && (
                      <span className="ml-3 px-2 py-0.5 text-[10px] font-medium rounded-full border border-nav-badge bg-nav-badge-bg text-nav-badge flex-shrink-0">
                        Current
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  }

  return (
    <div ref={rootRef} className="relative px-3 pt-4 pb-2" data-testid="firm-selector">
      {/* Dropdown Trigger - matches Figma design */}
      <button
        ref={triggerRef}
        type="button"
        onClick={handleClick}
        aria-expanded={hasDropdown ? isOpen : undefined}
        aria-disabled={isSingleFirmInert}
        tabIndex={isSingleFirmInert ? -1 : 0}
        className={`w-full flex items-center gap-3 py-2 rounded-xl border border-nav-tile-border appearance-none bg-transparent text-left ${
          hasDropdown ? 'cursor-pointer' : 'cursor-default'
        }`}
        data-testid="firm-selector-trigger"
      >
        {/* Firm Icon */}
        <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-5 h-5 text-nav-fg-secondary" />
        </div>

        {/* Firm Info */}
        <div className="flex-1 text-left min-w-0">
          <p className="text-sm font-semibold text-nav-fg truncate" data-testid="firm-selector-firm-name">
            {firmName || (profile
              // Profile loaded but the payload carries no firm name — show a static dash rather than
              // an animation that would pulse forever on a name that is never going to arrive.
              ? '—'
              // Still waiting on /me/profile — placeholder rather than the user's name.
              : <span className="block h-4 w-32 rounded bg-nav-tile animate-pulse" data-testid="firm-selector-firm-name-skeleton" />
            )}
          </p>
          <p className="text-xs text-nav-fg-secondary truncate" data-testid="firm-selector-user-role">{getPrimaryRole()}</p>
          {currentSite && (
            <p className="text-xs text-nav-fg-subtle truncate" data-testid="firm-selector-site-name">{currentSite.siteName}</p>
          )}
        </div>

        {/* Chevron - only when there is a dropdown to open */}
        {hasDropdown && (
          <ChevronUp
            className={`w-5 h-5 text-nav-chevron flex-shrink-0 mr-2 transition-transform ${isOpen ? '' : 'rotate-180'}`}
            data-testid="firm-selector-chevron"
          />
        )}
      </button>

      {/* Dropdown Menu - Firms List (absolute positioned to overlay on menu) */}
      {isOpen && hasDropdown && (
        <div
          className="absolute left-3 right-3 top-full mt-1 bg-nav-menu border border-nav-menu-edge rounded-xl overflow-hidden z-50 shadow-xl"
          data-testid="firm-selector-dropdown"
        >
          {dropdownContent}
        </div>
      )}
    </div>
  );
}
